import { ErrorLike } from "@apollo/client";
import { useApolloClient } from "@apollo/client/react";
import { HasIdAndName, StepperLayout, StepperLayoutStep, defaultTestId, useComputed } from "@homebound/beam";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { createMaterialCatalogUrl, createMaterialDetailsUrl, createMaterialPageUrl } from "src/RouteUrls";
import { Loading } from "src/components/Loading";
import { type AiFieldProposalFormValue } from "src/components/formFields/ai/utils/aiFieldProposalForm";
import { flattenEntityProposals, toAiFieldProposalFormValue } from "src/components/formFields/ai/utils/aiProposalForm";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { useUserFeatureFlagsQuery } from "src/contexts/FeatureFlags/FeatureFlagContext.generated";
import type { NamedFragment } from "src/fragments/commonFragments.generated";
import { AiFieldProposalStatus, AiProposalStatus, FeatureFlagType } from "src/generated/graphqlTypes";
import {
  MaterialCatalogMetadataDocument,
  MaterialCatalog_ItemFragment,
  MaterialCatalog_MaterialBrandFragment,
  useMaterialCatalogMetadataQuery,
} from "src/routes/libraries/material-catalog/MaterialCatalog.generated";
import type { MaterialListing_DetailFragment } from "src/routes/libraries/material-catalog/components/materialListingDetail.generated";
import { type MaterialListingFormState } from "src/routes/libraries/material-catalog/components/materialListingForm";
import {
  MaterialImportAiProposalQuery,
  useCreateMaterialImportAiProposalMutation,
  useMaterialImportAiProposalQuery,
} from "src/routes/libraries/material-catalog/material-import/MaterialImportPage.generated";
import { MaterialImportReviewStep } from "src/routes/libraries/material-catalog/material-import/components/MaterialImportReviewStep";
import {
  MaterialImportState,
  MaterialImportStep,
} from "src/routes/libraries/material-catalog/material-import/components/MaterialImportStep";
import { materialImportFormConfig } from "src/routes/libraries/material-catalog/material-import/materialImportForm";
import { MaterialListingPageDocument } from "src/routes/libraries/material-catalog/material-listing-page/MaterialListingPage.generated";
import {
  MaterialListingByListingIdProvider,
  useMaterialListingContext,
} from "src/routes/libraries/material-catalog/material-listing-page/components/material-listing-context/MaterialListingContext";
import { queryResult } from "src/utils";
import { useFormState } from "src/utils/formState";

const detailsStepLabel = "Details";

/**
 * The AI material import wizard: hand the agent a product page URL, then review what it found.
 *
 * Gated on `MATERIAL_IMPORT_AI`; without the flag, adding a material is still the hand-entry page.
 */
export function MaterialImportPage() {
  const isMaterialImportAiEnabled = useFeatureFlag(FeatureFlagType.MaterialImportAi);
  const { loading } = useUserFeatureFlagsQuery();

  if (loading) return <Loading />;
  if (!isMaterialImportAiEnabled) return <Navigate to={createMaterialPageUrl()} replace />;
  return <MaterialImportPageMetadata />;
}

function MaterialImportPageMetadata() {
  const query = useMaterialCatalogMetadataQuery();
  return queryResult(query, ({ items, materialBrands, materialVariantTags, markets }) => (
    <MaterialImportPageView items={items} brands={materialBrands} tags={materialVariantTags} markets={markets} />
  ));
}

type MaterialImportPageViewProps = {
  items: MaterialCatalog_ItemFragment[];
  brands: MaterialCatalog_MaterialBrandFragment[];
  tags: NamedFragment[];
  markets: HasIdAndName[];
};

function MaterialImportPageView(props: MaterialImportPageViewProps) {
  const { items, brands, tags, markets } = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const client = useApolloClient();
  const proposalId = searchParams.get("proposal") ?? undefined;

  const [createProposal] = useCreateMaterialImportAiProposalMutation();
  const { data, error, refetch, startPolling, stopPolling } = useMaterialImportAiProposalQuery({
    variables: { id: proposalId! },
    skip: !proposalId,
  });
  const proposal = proposalId ? data?.materialImportAiProposal : undefined;
  // Both are gated on `proposalId` so Try Again's param clear can't leave a stale proposal or error behind.
  const proposalError = proposalId ? error : undefined;
  const listingId = importedListingId(proposal);
  const importState = toImportState(proposalId, proposal, listingId, proposalError);

  importState === "processing" ? startPolling(10_000) : stopPolling();

  const formState = useFormState({ config: materialImportFormConfig });
  const canSubmitImport = useComputed(() => formState.valid, [formState]);

  const onSubmitImport = useCallback(async () => {
    const { itemId, manufacturerUrl } = formState.value;
    // Continue is gated on the form's validity, so this only guards the types.
    if (!itemId || !manufacturerUrl) return false;
    // TODO: Add some url validation
    const result = await createProposal({ variables: { input: { itemId, manufacturerUrl } } }).catch(() => undefined);
    const id = result?.data?.createMaterialImportAiProposal.id;
    if (!id) return false;
    setSearchParams({ proposal: id }, { replace: true });
    // The brand-new proposal isn't in this query's cache yet, so go get it
    await refetch({ id }).catch(() => undefined);
    return false;
  }, [createProposal, formState, refetch, setSearchParams]);

  // The agent creates whatever MAVs and brand it needs, but the selects resolve their current value
  // out of the option lists in `MaterialCatalogMetadata`, which we fetched before the agent ran. So
  // refetch once the import lands, or those attributes render blank until the user refreshes.
  useEffect(() => {
    if (!listingId) return;
    void client.refetchQueries({ include: [MaterialCatalogMetadataDocument] }).catch(() => undefined);
  }, [listingId, client]);

  const onResetImport = useCallback(() => {
    // Carry the failed attempt back into the form so the user can tweak the URL rather than retype it.
    // An unreadable proposal has nothing to carry, so this just drops the param.
    if (proposal) formState.set({ itemId: proposal.item.id, manufacturerUrl: proposal.manufacturerUrl });
    setSearchParams({}, { replace: true });
  }, [proposal, formState, setSearchParams]);

  const canSubmitFromImportStep = importState === "idle" || importState === "failed" || importState === "completed";

  const steps: StepperLayoutStep[] = [
    {
      label: "Import",
      // Continue stays disabled while the agent is running; once it lands it just moves along.
      primaryDisabled: !(canSubmitFromImportStep ? canSubmitImport : importState === "done"),
      onContinue: canSubmitFromImportStep ? onSubmitImport : undefined,
      content: (
        <MaterialImportStep
          formState={formState}
          items={items}
          state={importState}
          failureReason={failureReason(proposal, listingId, proposalError)}
          submitted={proposal && { itemName: proposal.item.name, manufacturerUrl: proposal.manufacturerUrl }}
          failureAction={{
            label: importState === "completed" ? "Start Another" : proposal ? "Try Again" : "Dismiss",
            onClick: onResetImport,
          }}
        />
      ),
    },
    {
      label: detailsStepLabel,
      disabled: !listingId,
      primaryDisabled: true,
      content: listingId ? (
        <MaterialImportReviewStep items={items} brands={brands} tags={tags} markets={markets} />
      ) : null,
    },
  ];

  // No listing yet means no listing form, so the wizard stands alone.
  if (!listingId) {
    return <MaterialImportStepper steps={steps} onComplete={() => {}} />;
  }

  return (
    <MaterialImportListing
      listingId={listingId}
      awaitingImagesForVariantIds={variantIdsExpectingImages(proposal)}
      listingFieldProposals={listingFieldProposals(proposal)}
      variantFieldProposals={variantFieldProposals(proposal)}
    >
      <MaterialImportReviewStepper steps={steps} listingId={listingId} />
    </MaterialImportListing>
  );
}

type MaterialImportListingProps = {
  listingId: string;
  /** The agent's proposals for the listing's own fields, so the review step can highlight them. */
  listingFieldProposals: AiFieldProposalFormValue[];
  /** The same, for each imported variant's own fields, keyed by variant id. */
  variantFieldProposals: Record<string, AiFieldProposalFormValue[]>;
  /** Imported variants whose photo is still downloading; the provider polls until they land. */
  awaitingImagesForVariantIds: string[];
  children: ReactNode;
};

/**
 * Owns the listing form state and variant selection for the review step
 */
function MaterialImportListing(props: MaterialImportListingProps) {
  const { listingId, awaitingImagesForVariantIds, listingFieldProposals, variantFieldProposals, children } = props;
  const [selectedVariantId, setSelectedVariantId] = useState<string | undefined>(undefined);
  const [createNew, setCreateNew] = useState(false);

  const onSelectVariant = useCallback((id: string | undefined) => {
    setCreateNew(false);
    setSelectedVariantId(id);
  }, []);

  const onCreateNew = useCallback(() => setCreateNew(true), []);

  const onListingLoaded = useCallback((listing: MaterialListing_DetailFragment) => {
    // Land on the default variant, the same one the detail page would have picked.
    setSelectedVariantId((current) => current ?? listing.defaultVariant?.id ?? listing.materialVariants.first?.id);
  }, []);

  return (
    <MaterialListingByListingIdProvider
      listingId={listingId}
      awaitingImagesForVariantIds={awaitingImagesForVariantIds}
      listingFieldProposals={listingFieldProposals}
      variantFieldProposals={variantFieldProposals}
      selectedVariantId={selectedVariantId}
      createNew={createNew}
      onSelectVariant={onSelectVariant}
      onCreateNew={onCreateNew}
      onListingLoaded={onListingLoaded}
    >
      {children}
    </MaterialListingByListingIdProvider>
  );
}

type MaterialImportReviewStepperProps = { steps: StepperLayoutStep[]; listingId: string };

/**
 * The wizard once the listing form is mounted
 */
function MaterialImportReviewStepper(props: MaterialImportReviewStepperProps) {
  const { steps: baseSteps, listingId } = props;
  const navigate = useNavigate();
  const client = useApolloClient();
  const { formState, save } = useMaterialListingContext();
  const canCreate = useComputed(() => formState.valid, [formState]);
  // Set once the save lands, so the hand-off below isn't treated as abandoning unsaved work.
  const saved = useRef(false);

  const onComplete = useCallback(async () => {
    // Touch the fields so any validation error is visible, the way `SubmitButton` does on the detail page.
    if (!formState.canSave()) return;
    // Values are already on the row from the import's creates; this only resolves pending statuses.
    acceptRemainingProposals(formState);
    if (!(await save())) return;
    saved.current = true;
    // The detail page reads this query cache-first, so refresh it or it gets the review step's
    // snapshot which might not have the images yet.
    await client.refetchQueries({ include: [MaterialListingPageDocument] }).catch(() => undefined);
    // `replace` so Back doesn't return to a `?proposal=` that has just been resolved.
    navigate(createMaterialDetailsUrl(listingId), { replace: true });
  }, [formState, save, client, navigate, listingId]);

  // The review step's validity lives on the listing form, which only exists inside this provider —
  // so the step is defined upstream and has its `primaryDisabled` filled in here.
  const steps = baseSteps.map((step) =>
    step.label === detailsStepLabel ? { ...step, primaryDisabled: !canCreate } : step,
  );

  return (
    <MaterialImportStepper
      steps={steps}
      listingId={listingId}
      onComplete={onComplete}
      // Beam's leave guard blocks the navigate above while this is true, and a saved form can stay
      // dirty (the resolved proposals aren't in the mutation's response to ack).
      isDirty={() => !saved.current && formState.dirty}
    />
  );
}

type MaterialImportStepperProps = {
  steps: StepperLayoutStep[];
  listingId?: string;
  onComplete: () => void | Promise<void>;
  isDirty?: () => boolean;
};

function MaterialImportStepper(props: MaterialImportStepperProps) {
  const { steps, listingId, onComplete, isDirty } = props;
  const navigate = useNavigate();

  return (
    <StepperLayout
      key={listingId ?? "import"}
      aiMode
      title="Add Material"
      breadcrumbs={{ breadcrumbs: { label: "Material Library", href: createMaterialCatalogUrl() } }}
      steps={steps}
      defaultStep={listingId ? defaultTestId(detailsStepLabel) : undefined}
      onCancel={() => navigate(createMaterialCatalogUrl())}
      completeLabel="Create"
      onComplete={onComplete}
      isDirty={isDirty}
    />
  );
}

/** Which body the wizard shows. */
function toImportState(
  proposalId: string | undefined,
  proposal: MaterialImportAiProposalQuery["materialImportAiProposal"] | undefined,
  listingId: string | undefined,
  error: ErrorLike | undefined,
): MaterialImportState {
  if (!proposalId) return "idle";
  if (error) return "failed";
  // Cold load of `?proposal=`: we don't know the status yet, and a running agent is the likely answer.
  if (!proposal) return "processing";
  if (proposal.status.code === AiProposalStatus.Pending) return "processing";
  // Fully resolved: the entity proposals are soft-deleted, so there is no listing left to point at.
  // Checked before `listingId` so a spent `?proposal=` doesn't read as "we found nothing".
  if (proposal.deletedAt) return "completed";
  return listingId ? "done" : "failed";
}

/**
 * The listing the import created, from its one `MATERIAL_LISTING` entity proposal.
 * Imports apply their creates optimistically, so a finished proposal already points at real rows.
 */
function importedListingId(
  proposal: MaterialImportAiProposalQuery["materialImportAiProposal"] | undefined,
): string | undefined {
  for (const { entity } of proposal?.listingProposals ?? []) {
    if (entity?.__typename === "MaterialListing") return entity.id;
  }
  return undefined;
}

/**
 * Marks every still-pending proposal Accepted, i.e. "the user reviewed this and left it alone".
 *
 * Without this they stay Pending forever: `resolveAiProposalStatus` only fires on the fields the
 * user actually edits, and nothing else resolves the rest.
 */
function acceptRemainingProposals(formState: MaterialListingFormState) {
  const rows = [...formState.fieldProposals.rows, ...formState.variants.rows.flatMap((v) => v.fieldProposals.rows)];
  for (const row of rows) {
    if (row.status.value === AiFieldProposalStatus.Pending) row.status.set(AiFieldProposalStatus.Accepted);
  }
}

/**
 * The agent's proposals for the listing's own fields, flattened for the form.
 */
function listingFieldProposals(
  proposal: MaterialImportAiProposalQuery["materialImportAiProposal"] | undefined,
): AiFieldProposalFormValue[] {
  return flattenEntityProposals(proposal?.listingProposals);
}

/**
 * The agent's proposals for each imported variant's own fields, keyed by variant id.
 */
function variantFieldProposals(
  proposal: MaterialImportAiProposalQuery["materialImportAiProposal"] | undefined,
): Record<string, AiFieldProposalFormValue[]> {
  const byVariantId: Record<string, AiFieldProposalFormValue[]> = {};
  for (const { entity, fieldProposals } of proposal?.variantProposals ?? []) {
    if (entity?.__typename !== "MaterialVariant") continue;
    byVariantId[entity.id] = fieldProposals.map(toAiFieldProposalFormValue);
  }
  return byVariantId;
}

/**
 * The imported variants the agent found a photo for.
 *
 * Images are downloaded on their own job queue *after* the proposal flips to Complete, so the review
 * step can open before they exist. An `imageUrl` field proposal is the record of "a photo is coming
 * for this variant", which is what lets us wait for exactly those rather than guessing.
 */
function variantIdsExpectingImages(
  proposal: MaterialImportAiProposalQuery["materialImportAiProposal"] | undefined,
): string[] {
  const ids: string[] = [];
  for (const { entity, fieldProposals } of proposal?.variantProposals ?? []) {
    // "imageUrl" is the name of the afp field we use to store the MVI asset url
    const expectsImage = fieldProposals.some((fp) => fp.fieldName === "imageUrl");
    if (expectsImage && entity?.__typename === "MaterialVariant") ids.push(entity.id);
  }
  return ids;
}

function failureReason(
  proposal: MaterialImportAiProposalQuery["materialImportAiProposal"] | undefined,
  listingId: string | undefined,
  error: ErrorLike | undefined,
): string | undefined {
  if (error) return error.message;
  if (!proposal || listingId || proposal.deletedAt) return undefined;
  if (proposal.failureReason) return proposal.failureReason;
  if (proposal.status.code === AiProposalStatus.Pending) return undefined;
  // The agent didn't find a material on the page, so the user needs to enter the details manually.
  return "We couldn't find a material on that page. Check the URL, or enter the details manually.";
}
